import { createContext, useContext, useMemo, useState, useCallback, useEffect, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/auth";
import { fetchWithAuth } from "@/lib/api";

const STORAGE_KEY = "fd_license_track";

/** Study programmes with fewer than this many questions are hidden from regular users (staff always sees all). */
export const CLIENT_VISIBLE_BANK_MIN_QUESTIONS = 100;

export type LicenseTrack = "ppl" | "cpl" | "pstar" | "roca";

export function isComplimentaryPracticeTrack(track: LicenseTrack): boolean {
  return track === "ppl" || track === "pstar" || track === "roca";
}

/** Timed exam simulation (CPAER-style) is only offered on the CPL programme. */
export function trackSupportsExamSimulation(track: LicenseTrack): boolean {
  return track === "cpl";
}

export interface TrackInfo {
  id: LicenseTrack;
  label: string;
  shortLabel: string;
  fullName: string;
  color: string;
  borderColor: string;
  bgColor: string;
  ringClass: string;
  hasQuestions: boolean;
}

export const TRACK_INFO: Record<LicenseTrack, TrackInfo> = {
  ppl: {
    id: "ppl",
    label: "PPL",
    shortLabel: "PPL",
    fullName: "Private Pilot Licence",
    color: "text-blue-400",
    borderColor: "border-blue-400/60",
    bgColor: "bg-blue-400/10",
    ringClass: "ring-blue-400",
    hasQuestions: true,
  },
  cpl: {
    id: "cpl",
    label: "CPL",
    shortLabel: "CPL",
    fullName: "Commercial Pilot Licence",
    color: "text-[#00ff00]",
    borderColor: "border-[#00ff00]",
    bgColor: "bg-[#00ff00]/10",
    ringClass: "ring-[#00ff00]",
    hasQuestions: true,
  },
  pstar: {
    id: "pstar",
    label: "PSTAR",
    shortLabel: "PSTAR",
    fullName: "Student Pilot Training / PSTAR prep",
    color: "text-sky-400",
    borderColor: "border-sky-400/60",
    bgColor: "bg-sky-400/10",
    ringClass: "ring-sky-400",
    hasQuestions: true,
  },
  roca: {
    id: "roca",
    label: "ROC-A",
    shortLabel: "ROC-A",
    fullName: "Restricted Operator Certificate — Aviation",
    color: "text-amber-400",
    borderColor: "border-amber-400/60",
    bgColor: "bg-amber-400/10",
    ringClass: "ring-amber-400",
    hasQuestions: true,
  },
};

/** Study programme order in UI and import: PPL → CPL → PSTAR → ROC-A */
export const TRACK_ORDER: LicenseTrack[] = ["ppl", "cpl", "pstar", "roca"];

export interface EnabledTracks {
  ppl: boolean;
  cpl: boolean;
  pstar: boolean;
  roca: boolean;
}

interface TrackContextValue {
  selectedTrack: LicenseTrack;
  setSelectedTrack: (track: LicenseTrack) => void;
  trackInfo: TrackInfo;
  enabledTracks: EnabledTracks;
  enabledTracksList: LicenseTrack[];
}

const TrackContext = createContext<TrackContextValue | null>(null);

function parseStoredTrack(raw: string | null): LicenseTrack | null {
  if (raw === "ppl" || raw === "cpl" || raw === "pstar" || raw === "roca") return raw;
  return null;
}

function isStaffRole(role: string | undefined): boolean {
  return role === "admin" || role === "master_admin" || role === "moderator";
}

export function TrackProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const staff = isStaffRole(user?.role);

  const { data: bankCounts } = useQuery({
    queryKey: ["track-bank-counts"],
    queryFn: () => fetchWithAuth("/api/stats/bank-counts") as Promise<Record<LicenseTrack, number>>,
    enabled: Boolean(user) && !staff,
    staleTime: 60_000,
  });

  const enabledTracksList = useMemo<LicenseTrack[]>(() => {
    if (!user || staff) return TRACK_ORDER;
    if (!bankCounts) return TRACK_ORDER;
    const list = TRACK_ORDER.filter((t) => (bankCounts[t] ?? 0) >= CLIENT_VISIBLE_BANK_MIN_QUESTIONS);
    return list.length > 0 ? list : TRACK_ORDER;
  }, [user, staff, bankCounts]);

  const enabledTracks = useMemo(
    () => ({
      ppl: enabledTracksList.includes("ppl"),
      cpl: enabledTracksList.includes("cpl"),
      pstar: enabledTracksList.includes("pstar"),
      roca: enabledTracksList.includes("roca"),
    }),
    [enabledTracksList],
  );

  const [selectedTrack, setSelectedTrackState] = useState<LicenseTrack>(() => {
    try {
      return parseStoredTrack(localStorage.getItem(STORAGE_KEY)) ?? "cpl";
    } catch {
      return "cpl";
    }
  });

  const setSelectedTrack = useCallback((track: LicenseTrack) => {
    setSelectedTrackState(track);
    try {
      localStorage.setItem(STORAGE_KEY, track);
    } catch {
      /* private mode */
    }
  }, []);

  useEffect(() => {
    if (!enabledTracksList.includes(selectedTrack)) {
      const next = enabledTracksList[0] ?? "cpl";
      setSelectedTrackState(next);
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        /* private mode */
      }
    }
  }, [enabledTracksList, selectedTrack]);

  const value = useMemo<TrackContextValue>(() => ({
    selectedTrack,
    setSelectedTrack,
    trackInfo: TRACK_INFO[selectedTrack],
    enabledTracks,
    enabledTracksList,
  }), [selectedTrack, setSelectedTrack, enabledTracks, enabledTracksList]);

  return (
    <TrackContext.Provider value={value}>
      {children}
    </TrackContext.Provider>
  );
}

export function useTrack() {
  const ctx = useContext(TrackContext);
  if (!ctx) throw new Error("useTrack must be used inside TrackProvider");
  return ctx;
}
